import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GiLotusFlower, GiCandleHolder } from 'react-icons/gi';
import { FaInstagram, FaWhatsapp } from 'react-icons/fa';
import ProductCard from '../components/ProductCard';
import Loader from '../components/Loader';

const API = process.env.REACT_APP_API_URL;

const LOGO_URL = "https://res.cloudinary.com/dghkmujye/image/upload/v1778060091/Picsart_26-05-05_11-57-45-870_1_eutaqj.png";

const HeroLogo = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.img
      src={LOGO_URL}
      alt="Swarn Dhoop"
      animate={scrolled ? {
        opacity: 0,
        scale: 0.2,
        y: -60,
      } : {
        opacity: 1,
        scale: 1,
        y: 0,
      }}
      transition={{
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      style={{
        objectFit: 'contain',
        borderRadius: '50%',
        display: 'block',
        margin: '0 auto',
        height: '120px',
        width: '120px',
        willChange: 'transform, opacity',
      }}
    />
  );
};

const Home = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/products`)
      .then(res => setProducts(Array.isArray(res.data) ? res.data.slice(0, 4) : []))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section style={{
        minHeight: '100vh', position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a0800 0%, #2C1810 40%, #4a2010 70%, #1a0800 100%)',
      }}>
        {/* Animated bg particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div key={i}
            animate={{ y: [-20, 20, -20], opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.5 }}
            style={{
              position: 'absolute',
              width: `${80 + i * 40}px`, height: `${80 + i * 40}px`,
              borderRadius: '50%',
              background: `radial-gradient(circle, rgba(212,175,55,0.15), transparent)`,
              left: `${10 + i * 15}%`, top: `${20 + (i % 3) * 20}%`,
            }} />
        ))}

        <div style={{ textAlign: 'center', zIndex: 2, padding: '0 2rem' }}>
          <HeroLogo />


          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            style={{ color: '#D4AF37', fontFamily: 'Cinzel', letterSpacing: '6px', fontSize: '0.85rem', marginTop: '1rem' }}>
            ॐ नमः शिवाय
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{
              fontFamily: 'Cinzel', fontSize: 'clamp(2.5rem, 7vw, 5rem)',
              color: '#F5D76E', letterSpacing: '6px', margin: '0.5rem 0',
              textShadow: '0 0 40px rgba(212,175,55,0.5)'
            }}>
            SWARN DHOOP
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            style={{ color: '#c9a96e', fontSize: '1.2rem', fontFamily: 'Crimson Text', fontStyle: 'italic', marginBottom: '2.5rem' }}>
            पवित्र सुगंध से सजाएं अपना मंदिर
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
            style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/products')}
              style={{
                background: 'linear-gradient(135deg, #D4AF37, #FF6B00)',
                color: '#fff', border: 'none', padding: '1rem 2.5rem',
                borderRadius: '50px', fontFamily: 'Cinzel', fontSize: '0.9rem',
                letterSpacing: '3px', cursor: 'pointer',
                boxShadow: '0 8px 25px rgba(212,175,55,0.4)'
              }}>
              VIEW PRODUCTS
            </motion.button>

            <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              href="https://wa.me/+918700248228" target="_blank" rel="noreferrer"
              style={{
                border: '1px solid #D4AF37', color: '#D4AF37',
                padding: '1rem 2.5rem', borderRadius: '50px',
                fontFamily: 'Cinzel', fontSize: '0.9rem',
                letterSpacing: '3px', textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: '8px'
              }}>
              <FaWhatsapp /> ORDER NOW
            </motion.a>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}
          style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)' }}>
          <div style={{ width: '1px', height: '50px', background: 'linear-gradient(to bottom, #D4AF37, transparent)', margin: '0 auto' }} />
        </motion.div>
      </section>

      {/* Features Strip */}
      <section style={{ background: '#2C1810', padding: '2rem', display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
        {[
          { icon: <GiCandleHolder size={24} />, text: '100% Natural' },
          { icon: <GiLotusFlower size={24} />, text: 'Handcrafted' },
          { icon: '🕉️', text: 'Spiritually Pure' },
          { icon: '🌸', text: 'Divine Fragrance' },
        ].map((f, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#F5D76E' }}>
            <span style={{ color: '#D4AF37' }}>{f.icon}</span>
            <span style={{ fontFamily: 'Cinzel', fontSize: '0.85rem', letterSpacing: '2px' }}>{f.text}</span>
          </motion.div>
        ))}
      </section>

      {/* Featured Products */}
      <section style={{ padding: '5rem 2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <GiLotusFlower size={32} color="#D4AF37" />
          <h2 style={{ fontFamily: 'Cinzel', fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', color: '#2C1810', letterSpacing: '4px', margin: '0.5rem 0' }}>
            OUR OFFERINGS
          </h2>
          <div className="divider" />
          <p style={{ color: '#8B6914', fontFamily: 'Crimson Text', fontSize: '1.1rem' }}>
            Handcrafted dhoop for your sacred rituals
          </p>
        </motion.div>

        {loading ? <Loader /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '2rem' }}>
            {products.map((p, i) => (
              <motion.div key={p._id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}>
                <ProductCard product={p} />
              </motion.div>
            ))}
          </div>
        )}

        {products.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/products')}
              style={{
                background: 'transparent', border: '1px solid #D4AF37',
                color: '#D4AF37', padding: '0.9rem 2.5rem', borderRadius: '50px',
                fontFamily: 'Cinzel', fontSize: '0.85rem', letterSpacing: '3px', cursor: 'pointer'
              }}>
              VIEW ALL PRODUCTS
            </motion.button>
          </div>
        )}
      </section>

      {/* Order CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #2C1810, #4a2010)',
        padding: '5rem 2rem', textAlign: 'center'
      }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}>
          <GiLotusFlower size={40} color="#D4AF37" />
          <h2 style={{ fontFamily: 'Cinzel', color: '#F5D76E', fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', letterSpacing: '4px', margin: '1rem 0' }}>
            READY TO ORDER?
          </h2>
          <p style={{ color: '#c9a96e', marginBottom: '2.5rem', fontSize: '1.1rem' }}>
            Reach us directly on WhatsApp or Instagram
          </p>
          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <motion.a whileHover={{ scale: 1.05, boxShadow: '0 8px 25px rgba(37,211,102,0.4)' }}
              href="https://wa.me/+918700248228" target="_blank" rel="noreferrer"
              style={{
                background: '#25D366', color: '#fff', padding: '1rem 2.5rem',
                borderRadius: '50px', textDecoration: 'none',
                fontFamily: 'Cinzel', letterSpacing: '2px', fontSize: '0.9rem',
                display: 'inline-flex', alignItems: 'center', gap: '10px'
              }}>
              <FaWhatsapp size={20} /> WHATSAPP
            </motion.a>
            <motion.a whileHover={{ scale: 1.05, boxShadow: '0 8px 25px rgba(225,48,108,0.4)' }}
              href="https://instagram.com/swarndhoop" target="_blank" rel="noreferrer"
              style={{
                background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                color: '#fff', padding: '1rem 2.5rem', borderRadius: '50px',
                textDecoration: 'none', fontFamily: 'Cinzel', letterSpacing: '2px', fontSize: '0.9rem',
                display: 'inline-flex', alignItems: 'center', gap: '10px'
              }}>
              <FaInstagram size={20} /> INSTAGRAM
            </motion.a>
          </div>
        </motion.div>
      </section>
      {/* Contact Section */}
      <section style={{ background: '#FDF6E3', padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}>
            <GiLotusFlower size={32} color="#D4AF37" />
            <h2 style={{
              fontFamily: 'Cinzel', fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              color: '#2C1810', letterSpacing: '4px', margin: '0.8rem 0'
            }}>
              CONTACT US
            </h2>
            <div className="divider" />

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '2rem', marginTop: '2.5rem'
            }}>

              {/* Address */}
              <motion.div whileHover={{ y: -5 }}
                style={{
                  background: 'linear-gradient(145deg, #fffdf5, #fdf0d0)',
                  border: '1px solid #e8c96d', borderRadius: '16px', padding: '2rem'
                }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.8rem' }}>📍</div>
                <h4 style={{ fontFamily: 'Cinzel', color: '#2C1810', marginBottom: '0.5rem', fontSize: '0.9rem', letterSpacing: '2px' }}>
                  ADDRESS
                </h4>
                <p style={{ color: '#6b4c1e', fontFamily: 'Crimson Text', fontSize: '1rem', lineHeight: 1.6 }}>
                  DDA Janta Flats, GTB Enclave,<br />North East Delhi - 110093
                </p>
              </motion.div>

              {/* Phone */}
              <motion.div whileHover={{ y: -5 }}
                style={{
                  background: 'linear-gradient(145deg, #fffdf5, #fdf0d0)',
                  border: '1px solid #e8c96d', borderRadius: '16px', padding: '2rem'
                }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.8rem' }}>📞</div>
                <h4 style={{ fontFamily: 'Cinzel', color: '#2C1810', marginBottom: '0.5rem', fontSize: '0.9rem', letterSpacing: '2px' }}>
                  CUSTOMER CARE
                </h4>
                <a href="tel:+918700248228"
                  style={{ color: '#D4AF37', fontFamily: 'Cinzel', fontSize: '1rem', textDecoration: 'none' }}>
                  +91 8700248228
                </a>
              </motion.div>

              {/* Email */}
              <motion.div whileHover={{ y: -5 }}
                style={{
                  background: 'linear-gradient(145deg, #fffdf5, #fdf0d0)',
                  border: '1px solid #e8c96d', borderRadius: '16px', padding: '2rem'
                }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.8rem' }}>✉️</div>
                <h4 style={{ fontFamily: 'Cinzel', color: '#2C1810', marginBottom: '0.5rem', fontSize: '0.9rem', letterSpacing: '2px' }}>
                  EMAIL
                </h4>
                <a href="mailto:swarndhoop@gmail.com"
                  style={{ color: '#D4AF37', fontFamily: 'Crimson Text', fontSize: '1rem', textDecoration: 'none' }}>
                  swarndhoop@gmail.com
                </a>
              </motion.div>

            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;